import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { FiX, FiSave, FiPhone, FiClock, FiMessageSquare, FiCalendar } = FiIcons;

const CallHistoryModal = ({ lead, onClose, onUpdateLead }) => {
  const [newCall, setNewCall] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    status: lead.status,
    comment: '',
    outcome: 'no-answer' // no-answer, answered, busy, voicemail, meeting-scheduled
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const callEntry = {
      date: newCall.date,
      time: newCall.time,
      status: newCall.status,
      comment: newCall.comment,
      outcome: newCall.outcome,
      timestamp: new Date(`${newCall.date}T${newCall.time}`).getTime()
    };

    const updatedCallHistory = [...(lead.callHistory || []), callEntry];
    
    await onUpdateLead(lead.id, {
      status: newCall.status,
      callHistory: updatedCallHistory,
      lastContact: new Date(`${newCall.date}T${newCall.time}`)
    });

    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCall(prev => ({ ...prev, [name]: value }));
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'answered': return 'bg-green-100 text-green-800';
      case 'no-answer': return 'bg-red-100 text-red-800';
      case 'busy': return 'bg-yellow-100 text-yellow-800';
      case 'voicemail': return 'bg-blue-100 text-blue-800';
      case 'meeting-scheduled': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'qualified': return 'bg-blue-100 text-blue-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiPhone} className="text-blue-500" />
              Call History - {lead.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Add New Call */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
              <SafeIcon icon={FiPhone} className="text-blue-600" />
              Log New Call
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <SafeIcon icon={FiCalendar} className="inline mr-1" />
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={newCall.date}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <SafeIcon icon={FiClock} className="inline mr-1" />
                    Time *
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={newCall.time}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call Outcome *
                  </label>
                  <select
                    name="outcome"
                    value={newCall.outcome}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="no-answer">No Answer</option>
                    <option value="answered">Answered</option>
                    <option value="busy">Busy</option>
                    <option value="voicemail">Voicemail</option>
                    <option value="meeting-scheduled">Meeting Scheduled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Update Status
                  </label>
                  <select
                    name="status"
                    value={newCall.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <SafeIcon icon={FiMessageSquare} className="inline mr-1" />
                  Call Notes/Comments
                </label>
                <textarea
                  name="comment"
                  value={newCall.comment}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add details about the call, next steps, or any important information..."
                />
              </div>
              <div className="flex gap-3">
                <motion.button
                  type="submit"
                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiSave} />
                  Save Call Log
                </motion.button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          {/* Call History */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-gray-600" />
              Call History ({lead.callHistory?.length || 0} calls)
            </h3>
            {lead.callHistory && lead.callHistory.length > 0 ? (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {lead.callHistory
                  .sort((a, b) => b.timestamp - a.timestamp)
                  .map((call, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">
                            {format(new Date(call.timestamp), 'MMM dd, yyyy')}
                          </span>
                          <span className="text-sm text-gray-600">
                            at {call.time}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getOutcomeColor(call.outcome)}`}>
                            {call.outcome.replace('-', ' ')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(call.status)}`}>
                            {call.status}
                          </span>
                        </div>
                      </div>
                      {call.comment && (
                        <p className="text-sm text-gray-700 bg-white p-2 rounded border">
                          {call.comment}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <SafeIcon icon={FiPhone} className="text-3xl mb-2 mx-auto opacity-50" />
                <p>No call history yet</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CallHistoryModal;